import type { Session, TenantBundle } from './types';
import { findDay, matches, minutesOf, shortMeta } from './selectors';

/**
 * The Call Sheet: an LLM-planned running order for one attendee, one day.
 *
 * Division of labour, and it matters: the model *chooses and explains*, the app
 * *owns every fact*. The model returns session ids and a one-line reason each.
 * Times, rooms, tracks and titles are always rendered from the CMS bundle, never
 * from the model's output. An attendee walking to a room a language model
 * invented is the failure mode this design exists to make impossible.
 */

export interface CallSheetPick {
  session: Session;
  /** The model's one-line case for this pick. Displayed, never trusted for facts. */
  reason: string;
}

export interface CallSheet {
  day: string;
  /** Short intro line the model writes for the day as a whole. */
  intro: string;
  picks: CallSheetPick[];
  /** True when this came from the offline fallback rather than the model. */
  fallback: boolean;
}

export interface CallSheetRequest {
  tenantId: string;
  day: string;
  interests: string[];
  /** Sessions already starred — the model works around them rather than over them. */
  saved: string[];
}

/** What the model is allowed to see. Deliberately small: ids, titles, tags, times. */
export interface CandidateSession {
  id: string;
  title: string;
  track: string;
  room: string;
  start: string;
  end: string;
  tags: string[];
  who: string[];
}

export function candidatesFor(bundle: TenantBundle, day: string): CandidateSession[] {
  return bundle.sessions
    .filter((s) => s.day === day)
    .map((s) => ({
      id: s.id,
      title: s.title,
      track: s.track,
      room: s.room,
      start: s.t,
      end: s.end,
      tags: s.tags,
      who: s.who.map((id) => bundle.talent.find((t) => t.id === id)?.name ?? id),
    }));
}

export const CALL_SHEET_SYSTEM = `You are planning one attendee's day at a fan convention.

You will get their interests, the sessions running that day, and any sessions they have already starred.

Choose 3 to 5 sessions that make a good day for this person, in chronological order. Prefer sessions matching their interests, but a day of six identical panels is a bad day — vary the track where you reasonably can, and leave room to eat and walk between rooms.

Never pick two sessions whose times overlap. Sessions they have already starred are fixed points: plan around them, and do not include them in your picks.

Respond with JSON only. No preamble, no markdown fences.

{
  "intro": "one sentence, under 20 words, about the shape of their day",
  "picks": [
    { "id": "session id exactly as given", "reason": "under 15 words, why this one for this person" }
  ]
}

Only use ids that appear in the session list. Never invent a session, a time, or a room.`;

export function buildCallSheetPrompt(
  bundle: TenantBundle,
  request: CallSheetRequest,
): string {
  const day = findDay(bundle, request.day);
  const candidates = candidatesFor(bundle, request.day);
  const starred = candidates.filter((c) => request.saved.includes(c.id));
  const available = candidates.filter((c) => !request.saved.includes(c.id));

  return [
    `Day: ${day.label} (${day.note})`,
    `Interests: ${request.interests.length ? request.interests.join(', ') : 'none given'}`,
    starred.length
      ? `Already starred (do not re-pick, plan around):\n${starred
          .map((c) => `- ${c.start}-${c.end} ${c.title} (${c.room})`)
          .join('\n')}`
      : 'Already starred: nothing yet',
    '',
    'Sessions available:',
    JSON.stringify(available, null, 1),
  ].join('\n');
}

/**
 * Turns raw model output into picks, discarding anything that does not
 * correspond to a real session or that collides with an earlier pick.
 */
export function parseCallSheet(
  bundle: TenantBundle,
  day: string,
  raw: string,
): CallSheet | null {
  const cleaned = raw.trim().replace(/^```(?:json)?/i, '').replace(/```$/, '').trim();

  let parsed: unknown;
  try {
    parsed = JSON.parse(cleaned);
  } catch {
    return null;
  }

  const obj = parsed as { intro?: unknown; picks?: unknown };
  if (!Array.isArray(obj.picks)) return null;

  const daySessions = bundle.sessions.filter((s) => s.day === day);
  const picks: CallSheetPick[] = [];

  for (const entry of obj.picks) {
    const item = entry as { id?: unknown; reason?: unknown };
    if (typeof item.id !== 'string') continue;

    const session = daySessions.find((s) => s.id === item.id);
    if (!session) continue;                                  // hallucinated id
    if (picks.some((p) => p.session.id === session.id)) continue;  // duplicate

    // Overlap guard. The prompt asks for this; we enforce it, because a
    // double-booked attendee is a worse outcome than a shorter list.
    const start = minutesOf(session.t);
    const end = minutesOf(session.end);
    const clashes = picks.some(
      (p) => start < minutesOf(p.session.end) && end > minutesOf(p.session.t),
    );
    if (clashes) continue;

    picks.push({
      session,
      reason: typeof item.reason === 'string' ? item.reason.slice(0, 120) : '',
    });
  }

  if (!picks.length) return null;

  picks.sort((a, b) => minutesOf(a.session.t) - minutesOf(b.session.t));

  return {
    day,
    intro: typeof obj.intro === 'string' ? obj.intro.slice(0, 160) : '',
    picks,
    fallback: false,
  };
}

/**
 * Interest-matching fallback, used when the model is unreachable or returns
 * nothing usable. Not as good, but the section always renders something — an
 * empty panel on the home screen reads as a broken app.
 */
export function localCallSheet(bundle: TenantBundle, request: CallSheetRequest): CallSheet {
  const daySessions = bundle.sessions
    .filter((s) => s.day === request.day && !request.saved.includes(s.id))
    .sort((a, b) => minutesOf(a.t) - minutesOf(b.t));

  const scored = daySessions.map((s) => ({
    session: s,
    score: (s.tags ?? []).filter((tag) => request.interests.includes(tag)).length,
  }));

  const picks: CallSheetPick[] = [];
  const seenTracks = new Set<string>();

  // Two passes: matching sessions first, then fill toward three with anything
  // that does not clash, preferring an unseen track.
  for (const pass of [1, 0]) {
    for (const { session, score } of scored) {
      if (picks.length >= 4) break;
      if (pass === 1 ? score === 0 : score > 0) continue;
      if (pass === 0 && picks.length >= 3) break;
      if (pass === 0 && seenTracks.has(session.track)) continue;

      const start = minutesOf(session.t);
      const end = minutesOf(session.end);
      const clashes = picks.some(
        (p) => start < minutesOf(p.session.end) && end > minutesOf(p.session.t),
      );
      if (clashes) continue;

      picks.push({ session, reason: score > 0 ? 'Matches your interests' : shortMeta(session) });
      seenTracks.add(session.track);
    }
  }

  picks.sort((a, b) => minutesOf(a.session.t) - minutesOf(b.session.t));

  return {
    day: request.day,
    intro: picks.length ? 'Picked from your interests while the planner is offline.' : '',
    picks,
    fallback: true,
  };
}

/**
 * Asks the CMS to plan the day.
 *
 * The Anthropic call happens server-side — the API key must never reach a
 * client bundle, where anyone can read it out of the JavaScript or unzip it
 * from the app package. On any failure this resolves to the local fallback
 * rather than rejecting, so callers have one code path.
 */
export async function fetchCallSheet(
  baseUrl: string,
  bundle: TenantBundle,
  request: CallSheetRequest,
  fetchImpl: typeof fetch = fetch,
  signal?: AbortSignal,
): Promise<CallSheet> {
  if (!baseUrl) return localCallSheet(bundle, request);

  try {
    const res = await fetchImpl(`${baseUrl.replace(/\/$/, '')}/itinerary`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(request),
      signal,
    });
    if (!res.ok) return localCallSheet(bundle, request);

    const data = (await res.json()) as { text?: string };
    if (typeof data.text !== 'string') return localCallSheet(bundle, request);

    return parseCallSheet(bundle, request.day, data.text) ?? localCallSheet(bundle, request);
  } catch {
    return localCallSheet(bundle, request);
  }
}

/** Cache key for a request — same interests on the same day is a very common hit. */
export function callSheetKey(request: CallSheetRequest): string {
  return [request.tenantId, request.day, [...request.interests].sort().join('|'), [...request.saved].sort().join('|')].join('::');
}

/** Whether the section has anything worth showing. */
export function hasUsefulPicks(sheet: CallSheet | null): sheet is CallSheet {
  return !!sheet && sheet.picks.length > 0;
}

export { matches as interestsMatch };
